import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Avatar
} from "@mui/material";
import axios from "axios";
import BASE_API_URL from "../apis/index";
import { useNavigate } from "react-router-dom";

function EditProfile() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    designation: "",
    profile_image: null,
  });
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    // Fetch existing profile data to prefill form
    const fetchProfile = async () => {
      try {
        const authToken = localStorage.getItem("authToken");
        if (!authToken) {
          setError("You are not logged in");
          return;
        }

        const res = await axios.get(`${BASE_API_URL}/api/user/profile`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        setFormData({
          name: res.data.name,
          company: res.data.company,
          designation: res.data.designation,
          profile_image: null, // we don't set file directly
        });
        setPreview(`${BASE_API_URL}/uploads/user_profile_images/${res.data.profile_image}`);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to load profile");
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, profile_image: file }));
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        setError("You are not logged in");
        setLoading(false);
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("company", formData.company);
      formDataToSend.append("designation", formData.designation);
      if (formData.profile_image) {
        formDataToSend.append("profile_image", formData.profile_image);
      }

      await axios.post(`${BASE_API_URL}/api/user/edit-profile`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess("Profile updated successfully!");
      setTimeout(() => {
        navigate('/profile');
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <Card sx={{ maxWidth: 600, p: 3, mt: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Edit Profile
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  className='w-100'
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  className='w-100'
                  label="Company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  className='w-100'
                  label="Designation"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  required
                />
              </Grid>

              {/* Profile Picture Upload */}
              <Grid item xs={12}>
                <Button variant="outlined" component="label">
                  Upload Profile Picture
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
              </Grid>

              {preview && (
                <Grid item xs={12}>
                  <Avatar
                    src={preview}
                    alt="Profile Preview"
                    sx={{ width: 100, height: 100 }}
                  />
                </Grid>
              )}
            </Grid>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 3 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Save Changes"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default EditProfile;
